import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import VirtualArtistRatesPanel from './rates/VirtualArtistRatesPanel';
import DefaultCommissionsPanel from './rates/DefaultCommissionsPanel';
import TaxesAndCurrencyPanel from './rates/TaxesAndCurrencyPanel';
import SpecializationsPanel from './rates/SpecializationsPanel';

type RatesTab = 'virtual' | 'commissions' | 'taxes' | 'specializations';

interface RatesViewProps {
  currentRole?: string | null;
}

const RatesView: React.FC<RatesViewProps> = ({ currentRole }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<RatesTab>('virtual');

  const tabs: Array<{ id: RatesTab; label: string; icon: React.ReactNode }> = [
    {
      id: 'virtual',
      label: t('rates.tabs.virtualArtists') || 'Виртуальные артисты',
      icon: <ICONS.Users className="w-5 h-5" />,
    },
    {
      id: 'commissions',
      label: t('rates.tabs.commissions') || 'Комиссии',
      icon: <ICONS.CreditCard className="w-5 h-5" />,
    },
    {
      id: 'taxes',
      label: t('rates.tabs.taxesCurrency') || 'Налоги и валюты',
      icon: <ICONS.Calculator className="w-5 h-5" />,
    },
    {
      id: 'specializations',
      label: t('rates.tabs.specializations') || 'Специализации',
      icon: <ICONS.Grid className="w-5 h-5" />,
    },
  ];

  // Only admin/super admin/producer can edit rates
  const canEdit = currentRole === 'Super Admin' || currentRole === 'Admin' || currentRole === 'Producer';

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-4 md:p-6">
      {/* Sidebar with tabs */}
      <div className="lg:w-64 flex-shrink-0">
        <div className="bg-gray-800 rounded-lg p-2">
          <h2 className="text-lg font-semibold text-white px-3 py-2 border-b border-gray-700 mb-2">
            {t('rates.title') || 'Настройки ставок'}
          </h2>
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }
                `}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Info box */}
        <div className="mt-4 bg-gray-800/50 rounded-lg p-4 text-xs text-gray-400">
          <p className="mb-2">
            <strong className="text-gray-300">{t('rates.info.title') || 'Информация:'}</strong>
          </p>
          <p>
            {t('rates.info.description') || 
              'Глобальные настройки применяются по умолчанию при создании новых проектов и смет. Их можно переопределить на уровне проекта.'}
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-800 rounded-lg">
          {activeTab === 'virtual' && (
            <VirtualArtistRatesPanel canEdit={canEdit} />
          )}
          {activeTab === 'commissions' && (
            <DefaultCommissionsPanel canEdit={canEdit} />
          )}
          {activeTab === 'taxes' && (
            <TaxesAndCurrencyPanel canEdit={canEdit} />
          )}
          {activeTab === 'specializations' && (
            <SpecializationsPanel canEdit={canEdit} />
          )}
        </div>
      </div>
    </div>
  );
};

export default RatesView;
